import React, { useState, useEffect } from 'react';
import { Button, Input, Modal, Table, Icon } from 'semantic-ui-react';
import { eventService, PostType } from '../../../services/eventService';

interface DuplicateModalProps {
  modalOpen: boolean;
  post: any;
  postType: PostType;
  onClose: () => void;
  onConfirm: (originalPostId: string) => void;
}

export const DuplicateModal: React.FC<DuplicateModalProps> = ({
  modalOpen,
  post,
  postType,
  onClose,
  onConfirm,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedOriginal, setSelectedOriginal] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  // Extract post.id for useEffect dependency
  const postId = post?.id;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!modalOpen) {
      setSearchTerm('');
      setSearchResults([]);
      setSelectedOriginal(null);
    }
  }, [modalOpen]);

  // Debounced search
  useEffect(() => {
    if (searchTerm.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setIsSearching(true);
      try {
        const results = await eventService.searchPosts(searchTerm, postType, postId);
        setSearchResults(results);
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, postType, postId]);

  const handleConfirm = () => {
    if (selectedOriginal) {
      onConfirm(selectedOriginal);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    setSearchResults([]);
    setSelectedOriginal(null);
    onClose();
  };

  return (
    <Modal open={modalOpen} size="large">
      <Modal.Header>
        Mark as Duplicate
        <Icon
          name="close"
          style={{ float: 'right', cursor: 'pointer' }}
          onClick={handleClose}
        />
      </Modal.Header>
      <Modal.Content>
        <p>
          <strong>Post to mark as duplicate:</strong> {post?.title}
        </p>
        <p>Search for the original post that this is a duplicate of:</p>
        <Input
          icon="search"
          placeholder="Search for original post..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fluid
          loading={isSearching}
        />

        {searchResults.length > 0 && (
          <Table selectable style={{ marginTop: '1rem' }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell style={{ width: '50px' }}>Select</Table.HeaderCell>
                <Table.HeaderCell>Title</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '150px' }}>Published Date</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {searchResults.map((result) => (
                <Table.Row
                  key={result.id}
                  active={selectedOriginal === result.id}
                  onClick={() => setSelectedOriginal(result.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Cell>
                    <input
                      type="radio"
                      checked={selectedOriginal === result.id}
                      onChange={() => setSelectedOriginal(result.id)}
                      aria-label={`Select ${result.title} as original`}
                      id={`original-${result.id}`}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <label htmlFor={`original-${result.id}`} style={{ cursor: 'pointer' }}>
                      {result.title}
                    </label>
                  </Table.Cell>
                  <Table.Cell>
                    {result.publishedAt
                      ? new Date(result.publishedAt).toLocaleDateString()
                      : '-'}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}

        {searchTerm.length >= 2 && searchResults.length === 0 && !isSearching && (
          <p style={{ marginTop: '1rem', color: 'gray' }}>No results found</p>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button primary disabled={!selectedOriginal} onClick={handleConfirm}>
          Mark as Duplicate
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
